import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useUpdateEffect } from '@framework/hooks';
import { addQueryParams, removeQueryParams } from '@framework/router/nextRouter';
import type { TFilterPayload } from '@/creditflowresearch/domain/vo/Bondscreener';
import {
  useRestrictedAccess,
} from '@/creditflowresearch/application/User/access/grants/useRestrictedAccess';
import { useCorporateBondScreenerDictionariesContext } from './CorporateBondScreenerDictionariesContext';
import { restrictToEuropeanMarket } from './buildCorporateBondScreenerRequest';
import { buildFilterQueryParams, parseFiltersFromQuery, SYNCED_QUERY_PARAMS } from './filterQuerySync';

const useFilterQuerySync = (initialFilters: TFilterPayload) => {
  const { query, replace } = useRouter();
  const skipQuerySync = useRef(false);
  const { industries } = useCorporateBondScreenerDictionariesContext();
  // TODO: remove when the European market opening period ends
  const isRestrictedToEuropeanMarket = useRestrictedAccess(true);

  const applyMarketRestriction = useCallback((filters: TFilterPayload): TFilterPayload => (
    isRestrictedToEuropeanMarket ? restrictToEuropeanMarket(filters) : filters
  ), [isRestrictedToEuropeanMarket]);

  const [filterPayload, setFilterPayload] = useState<TFilterPayload>(() => applyMarketRestriction({
    ...initialFilters,
    ...parseFiltersFromQuery(query, industries.getAll()),
  }));

  useUpdateEffect(() => {
    if (skipQuerySync.current) {
      skipQuerySync.current = false;
      return;
    }

    setFilterPayload((prev) => applyMarketRestriction({
      ...prev,
      ...parseFiltersFromQuery(query, industries.getAll()),
    }));
  }, [query.products, query.markets, query.segments, industries, applyMarketRestriction]);

  const syncQueryParams = useCallback((filters: TFilterPayload): void => {
    const nextParams = buildFilterQueryParams(filters, industries.getAll());
    const hasChanges = SYNCED_QUERY_PARAMS.some((key) => ((query[key] as string) || '') !== nextParams[key]);
    if (!hasChanges) return;

    skipQuerySync.current = true;
    const activeParams = Object.fromEntries(Object.entries(nextParams).filter(([, value]) => value));
    const newQuery = addQueryParams(removeQueryParams(query, SYNCED_QUERY_PARAMS), activeParams);
    replace({ query: newQuery }, undefined, { shallow: true });
  }, [query, replace, industries]);

  useEffect(() => {
    syncQueryParams(filterPayload);
  }, []);

  const applyFilters = useCallback((filters: TFilterPayload): void => {
    const restrictedFilters = applyMarketRestriction(filters);
    setFilterPayload(restrictedFilters);
    syncQueryParams(restrictedFilters);
  }, [applyMarketRestriction, syncQueryParams]);

  return { filterPayload, applyFilters };
};

export default useFilterQuerySync;
